import { useEffect, useMemo, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { Search, X } from "lucide-react";
import { downloadCSV, inr, Kpi, EmptyRow } from "./utils";

const AGE_BUCKETS = ["0-5", "6-10", "11-15", "16-20", "21-30", ">30"];

export default function JobsAgingReport() {
  const [customers, setCustomers] = useState([]);
  const [customerFilter, setCustomerFilter] = useState("all");
  const [period, setPeriod] = useState("all");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState("");
  const [view, setView] = useState("bucketed"); // bucketed | by-customer | by-bucket

  useEffect(() => {
    api.get("/reports/customers").then(({ data }) => setCustomers(data)).catch(() => {});
  }, []);

  const load = () => {
    setBusy(true);
    const params = {};
    if (customerFilter !== "all") params.customer_name = customerFilter;
    if (period !== "all") params.time_period = period;
    api.get("/reports/jobs-aging", { params })
      .then(({ data }) => setData(data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setBusy(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [customerFilter, period]);

  const rows = useMemo(() => {
    if (!data) return [];
    let list = data.rows;
    if (q.trim()) {
      const n = q.toLowerCase();
      list = list.filter((r) => (r.customer_name || "").toLowerCase().includes(n));
    }
    return list;
  }, [data, q]);

  const customerTotals = useMemo(() => {
    if (!data) return [];
    if (!q.trim()) return data.customer_totals;
    const n = q.toLowerCase();
    return data.customer_totals.filter((r) => (r.customer_name || "").toLowerCase().includes(n));
  }, [data, q]);

  const exportCSV = () => {
    if (view === "by-bucket") {
      downloadCSV(
        "jobs-aging-by-bucket",
        ["Time Period", "Planning", "Printing", "Inspection", "Slitting", "Packing", "No. of Jobs", "Value of Jobs in Hand"],
        (data?.bucket_totals || []).map((r) => [
          r.time_period, r.planning, r.printing, r.inspection, r.slitting, r.packing, r.no_of_jobs, r.value_of_jobs,
        ])
      );
    } else if (view === "by-customer") {
      downloadCSV(
        "jobs-aging-by-customer",
        ["Customer Name", "Planning", "Printing", "Inspection", "Slitting", "Packing", "No. of Jobs", "Value of Jobs in Hand"],
        customerTotals.map((r) => [
          r.customer_name, r.planning, r.printing, r.inspection, r.slitting, r.packing, r.no_of_jobs, r.value_of_jobs,
        ])
      );
    } else {
      downloadCSV(
        "jobs-aging-detail",
        ["Customer Name", "Time Period", "Planning", "Printing", "Inspection", "Slitting", "Packing", "No. of Jobs", "Value of Jobs in Hand"],
        rows.map((r) => [
          r.customer_name, r.time_period, r.planning, r.printing, r.inspection, r.slitting, r.packing, r.no_of_jobs, r.value_of_jobs,
        ])
      );
    }
  };

  const grand = data?.grand_total || {};

  return (
    <div>
      <div className="border border-neutral-200 bg-white p-4 mb-4 flex flex-wrap items-end gap-3">
        <div>
          <label className="label-caps block mb-1">Customer</label>
          <div className="flex items-center gap-1">
            <Select value={customerFilter} onValueChange={setCustomerFilter}>
              <SelectTrigger data-testid="aging-customer" className="rounded-sm w-72"><SelectValue placeholder="ALL CUSTOMERS" /></SelectTrigger>
              <SelectContent className="max-h-80">
                <SelectItem value="all">ALL CUSTOMERS</SelectItem>
                {customers.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
            {customerFilter !== "all" && (
              <button data-testid="aging-clear-customer" onClick={() => setCustomerFilter("all")}
                className="p-1.5 hover:bg-neutral-100 rounded-sm" title="Clear"><X size={14} /></button>
            )}
          </div>
        </div>
        <div>
          <label className="label-caps block mb-1">Time Period</label>
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger data-testid="aging-period" className="rounded-sm w-40"><SelectValue placeholder="Select Time period" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Select Time period</SelectItem>
              {AGE_BUCKETS.map((b) => <SelectItem key={b} value={b}>{b} Days</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="label-caps block mb-1">View</label>
          <Select value={view} onValueChange={setView}>
            <SelectTrigger data-testid="aging-view" className="rounded-sm w-56"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="bucketed">Customer × Time Period (detail)</SelectItem>
              <SelectItem value="by-customer">Customer summary</SelectItem>
              <SelectItem value="by-bucket">Time Period summary</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex-1 min-w-48">
          <label className="label-caps block mb-1">Search customer</label>
          <div className="relative">
            <Search size={14} className="absolute left-2.5 top-2.5 text-neutral-400" />
            <Input data-testid="aging-search" value={q} onChange={(e) => setQ(e.target.value)} className="pl-8 rounded-sm" placeholder="e.g. Gland" />
          </div>
        </div>
        <Button data-testid="aging-refresh" onClick={load} disabled={busy} variant="outline" className="rounded-sm">
          {busy ? "Loading…" : "Refresh"}
        </Button>
        <Button data-testid="aging-export" onClick={exportCSV} className="rounded-sm bg-[#FF5A00] hover:bg-[#e04f00]">
          Download .csv
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
        <Kpi label="Customers with jobs in hand" value={inr(grand.customers || 0)} />
        <Kpi label="Total jobs in hand" value={inr(grand.no_of_jobs || 0)} />
        <Kpi label="Value of jobs in hand" value={`₹${inr(grand.value_of_jobs || 0)}`} />
      </div>

      {/* Age-bucket clickable strip */}
      <div className="border border-neutral-200 bg-white p-4 mb-4">
        <div className="label-caps mb-2 text-[10px]">Jobs in hand by age bucket</div>
        <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-2 text-sm">
          <div className={`p-2 border cursor-pointer ${period === "all" ? "border-[#FF5A00] bg-orange-50" : "border-neutral-200"}`}
            onClick={() => setPeriod("all")} data-testid="aging-bucket-all">
            <div className="label-caps text-[10px] text-neutral-500">All ages</div>
            <div className="font-mono-num text-lg font-semibold">{inr(grand.no_of_jobs || 0)}</div>
          </div>
          {AGE_BUCKETS.map((b) => {
            const bt = (data?.bucket_totals || []).find((x) => x.time_period === b);
            return (
              <div key={b}
                className={`p-2 border cursor-pointer ${period === b ? "border-[#FF5A00] bg-orange-50" : "border-neutral-200"}`}
                onClick={() => setPeriod(period === b ? "all" : b)}
                data-testid={`aging-bucket-${b}`}>
                <div className="label-caps text-[10px] text-neutral-500">{b} Days</div>
                <div className="font-mono-num text-lg font-semibold">{inr(bt?.no_of_jobs || 0)}</div>
                <div className="text-[10px] text-neutral-500 mt-0.5">₹{inr(bt?.value_of_jobs || 0)}</div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="border border-neutral-200 bg-white overflow-x-auto">
        {view === "by-bucket" ? (
          <table className="w-full text-sm" data-testid="aging-bucket-table">
            <thead className="bg-neutral-50 label-caps">
              <tr>
                <th className="text-left px-4 py-2.5">Time Period</th>
                <th className="text-right px-4 py-2.5">Planning</th>
                <th className="text-right px-4 py-2.5">Printing</th>
                <th className="text-right px-4 py-2.5">Inspection</th>
                <th className="text-right px-4 py-2.5">Slitting</th>
                <th className="text-right px-4 py-2.5">Packing</th>
                <th className="text-right px-4 py-2.5">No. of Jobs</th>
                <th className="text-right px-4 py-2.5">Value of Jobs in Hand</th>
              </tr>
            </thead>
            <tbody>
              {(data?.bucket_totals || []).length === 0 && <EmptyRow cols={8} msg="No pending jobs." />}
              {(data?.bucket_totals || []).map((r) => (
                <tr key={r.time_period} className="border-t border-neutral-100 data-row" data-testid={`aging-brow-${r.time_period}`}>
                  <td className="px-4 py-2.5 font-mono-num">{r.time_period} Days</td>
                  <td className="px-4 py-2.5 text-right font-mono-num">{r.planning || "—"}</td>
                  <td className="px-4 py-2.5 text-right font-mono-num">{r.printing || "—"}</td>
                  <td className="px-4 py-2.5 text-right font-mono-num">{r.inspection || "—"}</td>
                  <td className="px-4 py-2.5 text-right font-mono-num">{r.slitting || "—"}</td>
                  <td className="px-4 py-2.5 text-right font-mono-num">{r.packing || "—"}</td>
                  <td className="px-4 py-2.5 text-right font-mono-num font-semibold">{inr(r.no_of_jobs)}</td>
                  <td className="px-4 py-2.5 text-right font-mono-num font-semibold">₹{inr(r.value_of_jobs)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : view === "by-customer" ? (
          <table className="w-full text-sm" data-testid="aging-customer-table">
            <thead className="bg-neutral-50 label-caps">
              <tr>
                <th className="text-left px-4 py-2.5">Customer Name</th>
                <th className="text-right px-4 py-2.5">Planning</th>
                <th className="text-right px-4 py-2.5">Printing</th>
                <th className="text-right px-4 py-2.5">Inspection</th>
                <th className="text-right px-4 py-2.5">Slitting</th>
                <th className="text-right px-4 py-2.5">Packing</th>
                <th className="text-right px-4 py-2.5">No. of Jobs</th>
                <th className="text-right px-4 py-2.5">Value of Jobs in Hand</th>
              </tr>
            </thead>
            <tbody>
              {customerTotals.length === 0 && <EmptyRow cols={8} msg="No pending jobs." />}
              {customerTotals.slice(0, 500).map((r) => (
                <tr key={r.customer_name} className="border-t border-neutral-100 data-row" data-testid={`aging-crow-${r.customer_name}`}>
                  <td className="px-4 py-2.5 font-medium">{r.customer_name}</td>
                  <td className="px-4 py-2.5 text-right font-mono-num">{r.planning || "—"}</td>
                  <td className="px-4 py-2.5 text-right font-mono-num">{r.printing || "—"}</td>
                  <td className="px-4 py-2.5 text-right font-mono-num">{r.inspection || "—"}</td>
                  <td className="px-4 py-2.5 text-right font-mono-num">{r.slitting || "—"}</td>
                  <td className="px-4 py-2.5 text-right font-mono-num">{r.packing || "—"}</td>
                  <td className="px-4 py-2.5 text-right font-mono-num font-semibold">{inr(r.no_of_jobs)}</td>
                  <td className="px-4 py-2.5 text-right font-mono-num font-semibold">₹{inr(r.value_of_jobs)}</td>
                </tr>
              ))}
              {customerTotals.length > 500 && (
                <tr><td colSpan={8} className="px-4 py-3 text-center text-neutral-400 text-xs">Showing first 500 of {customerTotals.length} rows. Export CSV for full list.</td></tr>
              )}
            </tbody>
          </table>
        ) : (
          <table className="w-full text-sm" data-testid="aging-detail-table">
            <thead className="bg-neutral-50 label-caps">
              <tr>
                <th className="text-left px-4 py-2.5">Customer Name</th>
                <th className="text-left px-4 py-2.5">Time Period</th>
                <th className="text-right px-4 py-2.5">Planning</th>
                <th className="text-right px-4 py-2.5">Printing</th>
                <th className="text-right px-4 py-2.5">Inspection</th>
                <th className="text-right px-4 py-2.5">Slitting</th>
                <th className="text-right px-4 py-2.5">Packing</th>
                <th className="text-right px-4 py-2.5">No. of Jobs</th>
                <th className="text-right px-4 py-2.5">Value of Jobs in Hand</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && <EmptyRow cols={9} msg="No pending jobs for this filter." />}
              {rows.slice(0, 800).map((r, i) => (
                <tr key={`${r.customer_name}-${r.time_period}-${i}`} className="border-t border-neutral-100 data-row" data-testid={`aging-drow-${r.customer_name}-${r.time_period}`}>
                  <td className="px-4 py-2.5 font-medium">{r.customer_name}</td>
                  <td className="px-4 py-2.5 font-mono-num text-xs">{r.time_period} Days</td>
                  <td className="px-4 py-2.5 text-right font-mono-num">{r.planning || "—"}</td>
                  <td className="px-4 py-2.5 text-right font-mono-num">{r.printing || "—"}</td>
                  <td className="px-4 py-2.5 text-right font-mono-num">{r.inspection || "—"}</td>
                  <td className="px-4 py-2.5 text-right font-mono-num">{r.slitting || "—"}</td>
                  <td className="px-4 py-2.5 text-right font-mono-num">{r.packing || "—"}</td>
                  <td className="px-4 py-2.5 text-right font-mono-num font-semibold">{inr(r.no_of_jobs)}</td>
                  <td className="px-4 py-2.5 text-right font-mono-num font-semibold">₹{inr(r.value_of_jobs)}</td>
                </tr>
              ))}
              {rows.length > 800 && (
                <tr><td colSpan={9} className="px-4 py-3 text-center text-neutral-400 text-xs">Showing first 800 of {rows.length} rows. Export CSV for full list.</td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>
      <p className="mt-3 text-xs text-neutral-500">
        Age is measured from <span className="font-medium">date_received</span> (fallback to <span className="font-medium">created_at</span>).
        Stage is derived from the job's current status (Planning covers all pre-print statuses).
      </p>
    </div>
  );
}


// ============================================================================
// 8. All Jobs in Hand (Priority-wise)
// ============================================================================
